import { useEffect, useRef } from "react";

/**
 * **The quick ring asking a tab to press one of its own buttons** (QR8,
 * STORY-221).
 *
 * The ring is a shortcut, never a new feature: *New note*, *Import images*,
 * *Take payment*, *To be done* and the rest already live inside a tab or the
 * tooth editor, behind state only that component holds. So the patient file
 * hands the component an ask, and the component runs **the same handler its
 * button runs** once it has loaded what that handler needs.
 *
 * `id` makes each ask distinct, so the same action chosen twice runs twice and
 * one ask never runs again on a later render.
 */
export interface RingAsk<Kind extends string> {
  kind: Kind;
  id: number;
}

let askCounter = 0;

/** A fresh id for a new ask. */
export function nextAskId(): number {
  askCounter += 1;
  return askCounter;
}

/**
 * Run `run` once per ask, as soon as `ready` (the data the button's handler
 * reads has loaded), then tell the owner it was honoured so it can clear it.
 * The latest `run` is always the one called, so it may read current state.
 */
export function useRingAsk<Ask extends RingAsk<string>>(
  ask: Ask | null | undefined,
  ready: boolean,
  run: (ask: Ask) => void,
  onAsked?: () => void,
): void {
  const done = useRef<number | null>(null);
  const latest = useRef(run);
  latest.current = run;

  useEffect(() => {
    if (ask === null || ask === undefined || !ready || done.current === ask.id) return;
    done.current = ask.id;
    latest.current(ask);
    onAsked?.();
  }, [ask, ready, onAsked]);
}
